import {ArtifactRef, ThreadItem} from './api/types';

// Pure helpers for the code runtime's files. No React, no fetch - covered by vitest.

export const RUNTIME_TOOL_PREFIX = 'runtime_';

export const isRuntimeTool = (toolName: string): boolean =>
    toolName.startsWith(RUNTIME_TOOL_PREFIX);

/** Hash hrefs used when a sandbox link cannot (yet) be turned into a real one. */
export const ARTIFACT_PLACEHOLDER = {
    processing: '#artifact-processing',
    error: '#artifact-error',
    missing: '#artifact-missing'
} as const;

export interface ArtifactMap {
    byPath: Map<string, ArtifactRef>;
    byFileName: Map<string, ArtifactRef>;
}

/**
 * Indexes every file the thread's runtime tool calls produced. Items are in
 * thread order, so a later version of the same path replaces an earlier one.
 */
export const buildArtifactMap = (items: ThreadItem[]): ArtifactMap => {
    const byPath = new Map<string, ArtifactRef>();
    const byFileName = new Map<string, ArtifactRef>();

    for (const item of items) {
        if (item.kind !== 'toolCall') {
            continue;
        }

        for (const artifact of item.artifacts ?? []) {
            if (artifact.sandboxPath) {
                byPath.set(artifact.sandboxPath, artifact);
            }

            byFileName.set(artifact.fileName, artifact);
        }
    }

    return {byPath, byFileName};
};

/** "sandbox:/mnt/data/a.csv", "/mnt/data/a.csv" or "a.csv" -> "/mnt/data/a.csv" */
export const normaliseSandboxPath = (reference: string): string | null => {
    let value = reference.trim().replace(/\\/g, '/');
    if (value.toLowerCase().startsWith('sandbox:')) {
        value = value.substring('sandbox:'.length);
    }

    try {
        value = decodeURIComponent(value);
    } catch {
        // Not URL-encoded - use as written
    }

    if (!value.startsWith('/')) {
        value = `/mnt/data/${value}`;
    }

    const segments = value.split('/').filter(segment => segment.length > 0);
    if (segments.some(segment => segment === '.' || segment === '..')) {
        return null;
    }

    const normalised = `/${segments.join('/')}`;
    return normalised.startsWith('/mnt/data/') ? normalised : null;
};

export const findArtifact = (
    map: ArtifactMap,
    reference: string
): ArtifactRef | undefined => {
    const path = normaliseSandboxPath(reference);
    if (!path) {
        return undefined;
    }

    return (
        map.byPath.get(path) ??
        map.byFileName.get(path.substring(path.lastIndexOf('/') + 1))
    );
};

// [text](sandbox:/mnt/data/x), ![alt](sandbox:/mnt/data/x) and [text](download:x),
// target optionally in <>
const SANDBOX_LINK =
    /(!?\[[^\]]*\]\()\s*<?((?:sandbox|download):[^)\s>]+)>?(\s+"[^"]*")?\s*\)/gi;

const DOWNLOAD_SCHEME = /^download:/i;

/**
 * Models often write links of the form sandbox:/mnt/data/file.ext out of habit, and
 * download:file.ext for a file they were told has a download link. react-markdown drops
 * unknown URL schemes, so they are rewritten beforehand: to the real download when the
 * file is known, otherwise to a placeholder that says why there is no link (still being
 * produced / could not be saved / unknown).
 */
export const rewriteSandboxLinks = (
    text: string,
    map: ArtifactMap,
    isStreaming: boolean
): string => {
    const lower = text.toLowerCase();
    if (!lower.includes('sandbox:') && !lower.includes('download:')) {
        return text;
    }

    return text.replace(
        SANDBOX_LINK,
        (_match, opening: string, target: string) => {
            const artifact = findArtifact(map, target.replace(DOWNLOAD_SCHEME, ''));

            let href: string;
            if (artifact?.downloadPath) {
                href = artifact.downloadPath;
            } else if (artifact?.error) {
                href = ARTIFACT_PLACEHOLDER.error;
            } else if (isStreaming) {
                href = ARTIFACT_PLACEHOLDER.processing;
            } else {
                href = ARTIFACT_PLACEHOLDER.missing;
            }

            return `${opening}${href})`;
        }
    );
};

const ARTIFACT_PATH =
    /\/api\/assistant-threads\/[0-9a-f-]{36}\/artifacts\/[0-9a-f-]{36}(?:\/[^?#]*)?/i;

/**
 * The API path of an artifact download inside a link, whatever origin the link
 * was written with (the model is given absolute URLs). Null for any other link.
 * Artifact downloads need the bearer token, so they cannot be plain anchors.
 */
export const artifactPathFromHref = (
    href: string | undefined | null
): string | null => {
    if (!href) {
        return null;
    }

    const match = ARTIFACT_PATH.exec(href);
    return match ? match[0] : null;
};

export const fileNameFromArtifactPath = (path: string): string => {
    const last = path.substring(path.lastIndexOf('/') + 1);
    try {
        return decodeURIComponent(last);
    } catch {
        return last;
    }
};

export const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) {
        return `${bytes} B`;
    }

    if (bytes < 1024 * 1024) {
        return `${(bytes / 1024).toFixed(1)} KB`;
    }

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};
